import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, AlertTriangle, CheckCircle2, BellRing, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDateTime, STATUS_COLORS } from "@/lib/helpers";
import { t } from "@/lib/i18n";
import { PhonePush } from "@/components/admin/PhonePush";

const POLL_MS = 15000;
export const NOTIFY_EVENT = "mline:notify";

export function useOnNotify(handler) {
  useEffect(() => {
    window.addEventListener(NOTIFY_EVENT, handler);
    return () => window.removeEventListener(NOTIFY_EVENT, handler);
  }, [handler]);
}

function beep(urgent) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = urgent ? [880, 660, 880] : [660, 990];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      o.connect(g);
      g.connect(ctx.destination);
      const at = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      o.start(at);
      o.stop(at + 0.17);
    });
  } catch {
    /* audio unavailable */
  }
}

export const notifText = (n) => {
  if (n.type === "late") return `${n.driver_name} nu a ajuns la încărcare (programat ${n.note})`;
  return n.type === "issue"
    ? `${n.driver_name} a raportat: ${t("ro", `issue_${n.issue_type}`)}${n.note ? ` – ${n.note}` : ""}`
    : `${n.driver_name}: ${t("ro", `status_${n.status}`)}`;
};

const urgent = (n) => n.type === "issue" || n.type === "late";

function Item({ n, onOpen }) {
  const color = n.type === "late" ? "#F59E0B" : n.type === "issue" ? "#EF4444" : STATUS_COLORS[n.status];
  const Icon = n.type === "late" ? Clock : n.type === "issue" ? AlertTriangle : CheckCircle2;
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`notification-${n.id}`}
      className={`flex w-full gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors last:border-0 hover:bg-white/[0.04] ${n.read ? "opacity-60" : ""}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} />
      <div className="min-w-0 flex-1">
        <div className="text-sm">{notifText(n)}</div>
        <div className="mt-0.5 font-mono text-[11px] text-slate-500">{n.order_ref || "fără referință"} · {fmtDateTime(n.created_at)}</div>
      </div>
      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />}
    </button>
  );
}

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const seen = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data: d } = await api.get("/notifications");
    const fresh = seen.current ? d.items.filter((n) => !n.read && !seen.current.has(n.id)) : [];
    seen.current = new Set(d.items.map((n) => n.id));
    setData(d);
    if (!fresh.length) return;
    window.dispatchEvent(new CustomEvent(NOTIFY_EVENT, { detail: fresh }));
    beep(fresh.some(urgent));
    fresh.slice(0, 3).forEach((n) => {
      const msg = notifText(n);
      if (urgent(n)) toast.error(msg);
      else toast.info(msg);
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const sys = new Notification("M-Line · " + (n.order_ref || "Comandă"), { body: msg, icon: "/icon-192.png", tag: n.id });
        sys.onclick = () => { window.focus(); nav(`/admin/orders/${n.order_id}`); };
      }
    });
  }, [nav]);

  useEffect(() => {
    load().catch(() => {});
    const timer = setInterval(() => load().catch(() => {}), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    load();
    nav(`/admin/orders/${n.order_id}`);
    window.dispatchEvent(new CustomEvent(NOTIFY_EVENT, { detail: [n] }));
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const enableDesktop = async () => {
    beep(false);
    setPerm(await Notification.requestPermission());
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-bell-btn" className="relative rounded-full p-2 text-slate-300 transition-colors hover:bg-white/5 hover:text-white" title="Alerte">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[10px] font-bold text-white">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,380px)] border-white/10 bg-[#111827] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <span className="font-display text-lg font-bold uppercase">Alerte</span>
          {data.unread > 0 && <button type="button" onClick={readAll} data-testid="notification-read-all-btn" className="text-xs text-blue-400 hover:underline">Marchează toate citite</button>}
        </div>
        <PhonePush />
        {perm === "default" && (
          <button type="button" onClick={enableDesktop} data-testid="enable-desktop-notifications-btn" className="flex w-full items-center gap-2 border-b border-white/10 bg-blue-600/10 px-4 py-2.5 text-left text-xs text-blue-300 hover:bg-blue-600/20">
            <BellRing className="h-4 w-4" /> Activează notificările pe ecran (și când lucrezi în alt tab)
          </button>
        )}
        <div className="max-h-[60vh] overflow-y-auto">
          {data.items.length ? data.items.map((n) => <Item key={n.id} n={n} onOpen={openItem} />) : (
            <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notifications-empty">Nicio alertă încă.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
